// Atlas embed parameters — see docs/specs/EMBED_PARAMS.md.
//
// Optional query parameters on the Atlas root route. Absent parameters must
// change nothing: every field below is null/false unless explicitly and
// validly supplied.
//
//   view=3d|wheel      initial view mode
//   theme=dark|light   forces the theme for THIS PAGE LOAD ONLY (never persisted);
//                      while framed, { type: 'atlas-theme', theme, bg? } switches it live
//   chrome=none|full   `none` renders the graph canvas alone
//   bg=#RRGGBB         overrides the composited page background
//   fit=<number>       3D camera fit padding (default 1.12), clamped 0.6-2
//   cx=<0..1>          horizontal centre of the 3D graph in the frame, clamped 0.1-0.9
//   label=<px>         3D embed: size node labels to about this many screen pixels
//                      at the fitted framing (clamped 8-24); zooming still scales them
//   lang=de|en         forces the language for THIS PAGE LOAD ONLY (never persisted);
//                      while framed, { type: 'atlas-locale', locale } switches it live
//   cy=<0..1>          vertical centre of the graph in the frame, clamped 0.1-0.9
//   gestures=graph     only gestures over the graph are the graph's; the empty
//                      canvas around it scrolls the host page (embedGestures.ts)
//   collections=none   hide the scent-collection labels at the frame edges
//   toggle=view        with chrome=none, show the small round wheel/3D toggle
//
// When framed, the page also posts { type: 'atlas-ready', features } to its
// parent once the graph has been drawn (see notifyAtlasReadyAfterPaint).
// `scope` and live selection sync live in src/lib/embedSync.ts.

import { setAtlasPageBackgroundOverride } from '@/lib/atlasTokens';

export const BG_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

export interface AtlasEmbedParams {
  /** 'three' | 'circle' | null (null = current behaviour) */
  initialView: 'three' | 'circle' | null;
  /** true = dark, false = light, null = visitor's own preference */
  themeOverride: boolean | null;
  /** true when chrome=none */
  chromeless: boolean;
  /** validated #RRGGBB or null */
  backgroundHex: string | null;
  /** 3D camera fit padding, clamped to FIT_RANGE, or null */
  fitPadding: number | null;
  /** horizontal centre of the 3D graph as a fraction of frame width, or null */
  centerX: number | null;
  /** 'de' | 'en' from lang=, or null = visitor's own preference */
  localeOverride: 'de' | 'en' | null;
  /** target on-screen label font size in px (3D), clamped to LABEL_PX_RANGE, or null */
  labelPx: number | null;
  /** vertical centre of the graph as a fraction of frame height, or null */
  centerY: number | null;
  /** 'graph' = only gestures over the graph are the graph's; null = all of them */
  gestures: 'graph' | null;
  /** true when toggle=view: show the round wheel/3D toggle even under chrome=none */
  viewToggle: boolean;
  /** true when collections=none: no scent-collection labels at the frame edges */
  hideCollections: boolean;
}

export const FIT_RANGE = [0.6, 2] as const;
export const CENTER_X_RANGE = [0.1, 0.9] as const;
export const LABEL_PX_RANGE = [8, 24] as const;

/** '#RRGGBB' (already validated) → 'H S% L%', the shape the CSS tokens use. */
export function hexToHslTriplet(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const round = (n: number) => Math.round(n * 1000) / 1000;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

const DECIMAL_PATTERN = /^(\d+\.?\d*|\.\d+)$/;

// A plain non-negative decimal clamped into range; anything else is null.
function parseClamped(raw: string | null, [min, max]: readonly [number, number]): number | null {
  if (!raw || !DECIMAL_PATTERN.test(raw)) return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.min(max, Math.max(min, value));
}

const EMPTY: AtlasEmbedParams = {
  initialView: null,
  themeOverride: null,
  chromeless: false,
  backgroundHex: null,
  fitPadding: null,
  centerX: null,
  localeOverride: null,
  labelPx: null,
  centerY: null,
  gestures: null,
  viewToggle: false,
  hideCollections: false,
};

export function parseAtlasEmbedParams(search: string): AtlasEmbedParams {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return EMPTY;
  }

  const view = params.get('view');
  const theme = params.get('theme');
  const chrome = params.get('chrome');
  const bg = params.get('bg');
  const lang = params.get('lang');

  return {
    initialView: view === '3d' ? 'three' : view === 'wheel' ? 'circle' : null,
    themeOverride: theme === 'dark' ? true : theme === 'light' ? false : null,
    chromeless: chrome === 'none',
    // Only a strict #RRGGBB literal is ever accepted; anything else falls back
    // to the default. The validated value is a colour literal, never raw input.
    backgroundHex: bg && BG_HEX_PATTERN.test(bg) ? bg : null,
    fitPadding: parseClamped(params.get('fit'), FIT_RANGE),
    centerX: parseClamped(params.get('cx'), CENTER_X_RANGE),
    localeOverride: lang === 'de' || lang === 'en' ? lang : null,
    labelPx: parseClamped(params.get('label'), LABEL_PX_RANGE),
    centerY: parseClamped(params.get('cy'), CENTER_X_RANGE),
    gestures: params.get('gestures') === 'graph' ? 'graph' : null,
    viewToggle: params.get('toggle') === 'view',
    hideCollections: params.get('collections') === 'none',
  };
}

// Parsed once, from the URL the document was loaded with, so WebGL layers can
// composite against the right background from their very first frame.
export const ATLAS_EMBED: AtlasEmbedParams =
  typeof window === 'undefined' ? EMPTY : parseAtlasEmbedParams(window.location.search);

/**
 * Point every view at an embed ground: `bg=` at load, or the `bg` of a live
 * `atlas-theme` message. Takes a validated #RRGGBB only.
 */
export function applyEmbedBackground(hex: string): void {
  setAtlasPageBackgroundOverride(hex);
  // The 2D wheel paints its ground from the --graph-background token, not from
  // the WebGL override, so point the token at the same colour. Without this,
  // switching to the wheel inside an embed shows the Atlas's own ground as a
  // rectangle on the host page.
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--graph-background', hexToHslTriplet(hex));
  }
}

if (ATLAS_EMBED.backgroundHex) applyEmbedBackground(ATLAS_EMBED.backgroundHex);

/**
 * Optional embed capabilities this build supports, announced in `atlas-ready`
 * so a host can feature-detect them (see docs/specs/EMBED_PARAMS.md).
 * `scope` = the ?scope= param, `select` = atlas-select / atlas-selection,
 * `locale` = the ?lang= param and the live atlas-locale message,
 * `label` = the ?label= screen-size target for 3D labels,
 * `gestures` = ?gestures=graph with `atlas-scroll`, `toggle` = ?toggle=view,
 * `cy` and `collections` = those params, `view` = the live atlas-view message,
 * `theme` = the live atlas-theme message (with its optional `bg`),
 * `weights` = the `weights=` parameter (src/lib/embedSync.ts).
 */
export const ATLAS_EMBED_FEATURES: readonly string[] = [
  'scope',
  'select',
  'locale',
  'label',
  'gestures',
  'toggle',
  'cy',
  'collections',
  'view',
  'theme',
  'weights',
];

let atlasReadySent = false;

/**
 * Tell a hosting page that the graph is on screen. Posted at most once per
 * page load, only when framed, after two animation frames so the frame that
 * triggered it has actually been painted. The iframe `load` event fires long
 * before the graph draws, so hosts should fade their placeholder on this.
 */
export function notifyAtlasReadyAfterPaint(): void {
  if (atlasReadySent || typeof window === 'undefined' || window.parent === window) return;
  atlasReadySent = true;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      try {
        window.parent.postMessage({ type: 'atlas-ready', features: ATLAS_EMBED_FEATURES }, '*');
      } catch { /* noop */ }
    });
  });
}
